import React, { useState, useEffect } from 'react';
import { Search, Loader, ChevronDown, Plus, PlayCircle } from 'lucide-react';
import { useSofteamboardStore } from '../store/softeamboardStore';
import { Modal, Alert } from './shared';

const WORKFLOW_TYPES = [
  { value: 'salesforce_opportunity_create', label: 'Opportunity Create', needsCandidate: false },
  { value: 'salesforce_product_add', label: 'Product Add', needsCandidate: false },
  { value: 'salesforce_mission_create', label: 'Mission Create', needsCandidate: false },
  { value: 'salesforce_proposition_create', label: 'Proposition Create', needsCandidate: true },
  { value: 'salesforce_presentation_update', label: 'Presentation Update', needsCandidate: true },
  { value: 'salesforce_retention_update', label: 'Retention Update', needsCandidate: true },
];

const CreateWorkflowDialog = ({ open, onClose, onSuccess }) => {
  const {
    needs: storeNeeds,
    getCandidatesForNeed,
    loading: storeLoading,
    initialized: storeInitialized,
    createWorkflowRequest,
    triggerWorkflowRequest,
  } = useSofteamboardStore();

  const [selectedNeed, setSelectedNeed] = useState(null);
  const [needsInputValue, setNeedsInputValue] = useState('');
  const [showNeedsDropdown, setShowNeedsDropdown] = useState(false);
  const [workflowType, setWorkflowType] = useState('salesforce_opportunity_create');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Candidate fields
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [expectedTjm, setExpectedTjm] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');

  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    if (!open) {
      setSelectedNeed(null);
      setNeedsInputValue('');
      setWorkflowType('salesforce_opportunity_create');
      setError(null);
      setSelectedCandidateId('');
      setExpectedTjm('');
      setInterviewDate('');
      setInterviewTime('');
      setCandidates([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedNeed) {
      const c = getCandidatesForNeed(selectedNeed.id);
      setCandidates(c || []);
      setExpectedTjm(selectedNeed.tjm ? String(selectedNeed.tjm) : '');
    } else {
      setCandidates([]);
    }
  }, [selectedNeed, getCandidatesForNeed]);

  const filteredNeeds = storeNeeds.filter((need) => {
    if (!needsInputValue) return true;
    const term = needsInputValue.toLowerCase();
    return (
      (need.title && need.title.toLowerCase().includes(term)) ||
      (need.client && need.client.toLowerCase().includes(term))
    );
  });

  const handleNeedSelect = (need) => {
    setSelectedNeed(need);
    setNeedsInputValue(`${need.title} - ${need.client}`);
    setShowNeedsDropdown(false);
  };

  const currentType = WORKFLOW_TYPES.find(t => t.value === workflowType);

  const buildPayload = () => {
    const payload = {};
    if (selectedNeed) {
      payload.needId = selectedNeed.id;
      payload.needData = {
        title: selectedNeed.title,
        client: selectedNeed.client,
        company: selectedNeed.jdCompany,
        projectName: selectedNeed.jdProjectName,
        orderClients: selectedNeed.orderClients,
        priority: selectedNeed.priority,
        description: selectedNeed.description,
        tjm: selectedNeed.tjm,
      };
    }

    if (currentType?.needsCandidate && selectedCandidateId) {
      payload.candidateId = selectedCandidateId;
      const candidate = candidates.find(c => c.id === selectedCandidateId);
      if (candidate) {
        payload.candidateName = candidate.name;
        payload.candidateSalesforceId = candidate.salesforceId;
      }
    }

    if (workflowType === 'salesforce_proposition_create' && expectedTjm) {
      payload.expectedTjm = Number.parseFloat(expectedTjm);
    }
    if (workflowType === 'salesforce_presentation_update') {
      if (interviewDate) payload.interviewDate = interviewDate;
      if (interviewTime) payload.interviewTime = interviewTime;
    }

    return payload;
  };

  const validate = () => {
    if (!selectedNeed) return 'Please select a need';
    if (currentType?.needsCandidate && !selectedCandidateId) return 'Please select a candidate';
    if (workflowType === 'salesforce_proposition_create' && (!expectedTjm || Number.parseFloat(expectedTjm) <= 0)) {
      return 'Expected TJM must be greater than 0';
    }
    return null;
  };

  const handleCreate = async (triggerAfter = false) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        needId: selectedNeed.id,
        type: workflowType,
        payload: buildPayload(),
      };
      const created = await createWorkflowRequest(data);

      if (triggerAfter && created?.id) {
        await triggerWorkflowRequest(created.id);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create workflow request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Workflow Request"
      subtitle="Create a new workflow request for a need"
      icon={Plus}
      maxWidth="max-w-lg"
    >
      <div className="p-6">
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        <div className="space-y-4 mt-4">
          {/* Need Selection */}
          <div>
            <label htmlFor="create-workflow-need" className="block text-sm font-medium text-gray-700 mb-1">Need *</label>
            <div className="relative">
              <input id="create-workflow-need"
                type="text"
                value={needsInputValue}
                onChange={(e) => {
                  setNeedsInputValue(e.target.value);
                  setShowNeedsDropdown(true);
                  if (!e.target.value) setSelectedNeed(null);
                }}
                onFocus={() => setShowNeedsDropdown(true)}
                placeholder="Search for a need..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />

              {showNeedsDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {storeLoading ? (
                    <div className="p-3 text-center">
                      <Loader className="animate-spin h-4 w-4 mx-auto text-gray-400" />
                    </div>
                  ) : filteredNeeds.length > 0 ? (
                    filteredNeeds.slice(0, 20).map((need) => (
                      <button
                        key={need.id}
                        type="button"
                        onClick={() => handleNeedSelect(need)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                      >
                        <div className="font-medium text-gray-900 text-sm">
                          {need.title} - {need.client}
                        </div>
                        <div className="text-xs text-gray-500">
                          {need.tjm ? `TJM: ${need.tjm}` : 'No TJM'} | {need.priority}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-center text-gray-500 text-sm">
                      {storeInitialized ? 'No needs found' : 'Loading needs...'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Workflow Type */}
          <div>
            <label htmlFor="create-workflow-type" className="block text-sm font-medium text-gray-700 mb-1">Workflow Type *</label>
            <div className="relative">
              <select id="create-workflow-type"
                value={workflowType}
                onChange={(e) => {
                  setWorkflowType(e.target.value);
                  setSelectedCandidateId('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                {WORKFLOW_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Candidate Selection */}
          {currentType?.needsCandidate && (
            <div>
              <label htmlFor="create-workflow-candidate" className="block text-sm font-medium text-gray-700 mb-1">Candidate *</label>
              <select id="create-workflow-candidate"
                value={selectedCandidateId}
                onChange={(e) => setSelectedCandidateId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a candidate</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || `Candidate ${c.id.substring(0, 8)}`}
                  </option>
                ))}
              </select>
              {selectedNeed && candidates.length === 0 && (
                <p className="text-xs text-yellow-600 mt-1">No candidates attached to this need</p>
              )}
            </div>
          )}

          {/* Proposition: Expected TJM */}
          {workflowType === 'salesforce_proposition_create' && (
            <div>
              <label htmlFor="create-workflow-expected-tjm" className="block text-sm font-medium text-gray-700 mb-1">Expected TJM *</label>
              <input id="create-workflow-expected-tjm"
                type="number"
                value={expectedTjm}
                onChange={(e) => setExpectedTjm(e.target.value)}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Presentation: Interview Date + Time */}
          {workflowType === 'salesforce_presentation_update' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="create-workflow-interview-date" className="block text-sm font-medium text-gray-700 mb-1">Interview Date</label>
                <input id="create-workflow-interview-date"
                  type="date"
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="create-workflow-interview-time" className="block text-sm font-medium text-gray-700 mb-1">Interview Time</label>
                <input id="create-workflow-interview-time"
                  type="time"
                  value={interviewTime}
                  onChange={(e) => setInterviewTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleCreate(false)}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create
          </button>
          <button
            type="button"
            onClick={() => handleCreate(true)}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            Create & Trigger
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateWorkflowDialog;
